/**
 * Estimation du nombre de sujets d'examen à imprimer.
 * Affiche par pôle et niveau : matière, options concernées, effectifs, copies avec marge.
 * Permet l'impression groupée par pôle/niveau.
 */

import React, { useState, useMemo } from 'react';
import {
  Loader2, AlertCircle, Printer, FileText, Users, ChevronDown, ChevronUp,
  Settings2, BookOpen, TrendingUp, Filter,
} from 'lucide-react';
import {
  useGetEstimationCopiesQuery,
  EstimationCreneauDetail,
} from '../../services/emploisTempsApi';

interface Props {
  sessionId: number;
  isDark: boolean;
}

// Mapping pôle code → label lisible
const POLE_LABELS: Record<string, string> = {
  polytechnique: 'Pôle Polytechnique',
  poly: 'Pôle Polytechnique',
  commerce: 'Pôle Commerce',
  com: 'Pôle Commerce',
  juridique: 'Pôle Juridique',
  jur: 'Pôle Juridique',
};

const getPoleLabel = (codes: string[]): string => {
  if (!codes || codes.length === 0) return 'Pôle Non défini';
  for (const c of codes) {
    const lower = c.toLowerCase();
    for (const [key, label] of Object.entries(POLE_LABELS)) {
      if (lower.includes(key)) return label;
    }
  }
  return codes.join(', ');
};

const POLE_COLORS: Record<string, { bg: string; text: string; border: string; header: string }> = {
  'Pôle Polytechnique': { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200', header: '#92400E' },
  'Pôle Commerce': { bg: 'bg-teal-50', text: 'text-teal-800', border: 'border-teal-200', header: '#0F766E' },
  'Pôle Juridique': { bg: 'bg-rose-50', text: 'text-rose-800', border: 'border-rose-200', header: '#BE123C' },
};

const POLE_COLORS_DARK: Record<string, { bg: string; text: string; border: string }> = {
  'Pôle Polytechnique': { bg: 'bg-amber-900/20', text: 'text-amber-300', border: 'border-amber-700' },
  'Pôle Commerce': { bg: 'bg-teal-900/20', text: 'text-teal-300', border: 'border-teal-700' },
  'Pôle Juridique': { bg: 'bg-rose-900/20', text: 'text-rose-300', border: 'border-rose-700' },
};

const NIVEAU_COLORS: Record<string, string> = {
  L1: 'bg-blue-100 text-blue-700',
  L2: 'bg-green-100 text-green-700',
  L3: 'bg-purple-100 text-purple-700',
};

interface GroupedData {
  [pole: string]: {
    [niveau: string]: EstimationCreneauDetail[];
  };
}

const EstimationSujets: React.FC<Props> = ({ sessionId, isDark }) => {
  const [marge, setMarge] = useState(15);
  const [typeFormation, setTypeFormation] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [fontScale, setFontScale] = useState(100);

  const { data, isLoading, error } = useGetEstimationCopiesQuery({
    sessionId,
    marge,
    type_formation: typeFormation || undefined,
  });

  // Grouper par pôle → niveau → matières (dédupliquées par matiere_nom)
  const grouped = useMemo<GroupedData>(() => {
    if (!data?.details) return {};
    const result: GroupedData = {};
    for (const d of data.details) {
      const pole = getPoleLabel(d.pole_codes);
      if (!result[pole]) result[pole] = {};
      if (!result[pole][d.niveau]) result[pole][d.niveau] = [];
      result[pole][d.niveau].push(d);
    }
    return result;
  }, [data]);

  // Stats par pôle
  const poleStats = useMemo(() => {
    const stats: Record<string, { effectif: number; copies: number; matieres: number }> = {};
    for (const [pole, niveaux] of Object.entries(grouped)) {
      let effectif = 0, copies = 0, matieres = 0;
      for (const details of Object.values(niveaux)) {
        for (const d of details) {
          effectif += d.effectif;
          copies += d.copies_avec_marge;
          matieres++;
        }
      }
      stats[pole] = { effectif, copies, matieres };
    }
    return stats;
  }, [grouped]);

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    const allKeys: string[] = [];
    for (const [pole, niveaux] of Object.entries(grouped)) {
      for (const niv of Object.keys(niveaux)) {
        allKeys.push(`${pole}__${niv}`);
      }
    }
    setExpandedSections(new Set(allKeys));
  };

  const collapseAll = () => setExpandedSections(new Set());

  // ── Impression par pôle et niveau ──
  const handlePrint = () => {
    if (!data) return;
    const win = window.open('', '_blank');
    if (!win) return;

    const NAVY = '#1A2E4A';
    const GOLD = '#FFD700';
    const s = (basePt: number) => `${(basePt * fontScale / 100).toFixed(1)}pt`;

    let contentHtml = '';

    // Pour chaque pôle
    const sortedPoles = Object.keys(grouped).sort();
    for (const pole of sortedPoles) {
      const niveaux = grouped[pole];
      const ps = poleStats[pole];
      const poleColor = POLE_COLORS[pole]?.header || NAVY;

      contentHtml += `<div style="page-break-inside: avoid; margin-bottom: 24px;">`;
      contentHtml += `<div style="background:${poleColor};color:#fff;padding:10px 16px;border-radius:6px 6px 0 0;font-size:${s(12)};font-weight:700;">
        ${pole}
        <span style="float:right;font-size:${s(9)};font-weight:400;color:${GOLD};">
          ${ps.matieres} matières · ${ps.effectif} étudiants · ${ps.copies} copies (avec marge)
        </span>
      </div>`;

      // Pour chaque niveau dans ce pôle
      for (const niv of ['L1', 'L2', 'L3'].filter(n => niveaux[n])) {
        const details = niveaux[niv];
        const nivEff = details.reduce((s, d) => s + d.effectif, 0);
        const nivCop = details.reduce((s, d) => s + d.copies_avec_marge, 0);

        const nivBg = niv === 'L1' ? '#DBEAFE' : niv === 'L2' ? '#DCFCE7' : '#F3E8FF';
        const nivFg = niv === 'L1' ? '#1E40AF' : niv === 'L2' ? '#166534' : '#6B21A8';

        contentHtml += `<div style="background:${nivBg};color:${nivFg};padding:6px 16px;font-size:${s(10)};font-weight:700;border-left:4px solid ${nivFg};margin-top:2px;">
          ${niv} — ${details.length} matières · ${nivEff} étudiants · ${nivCop} copies
        </div>`;

        contentHtml += `<table>
          <thead><tr>
            <th style="text-align:left;width:4%;">N°</th>
            <th style="text-align:left;width:22%;">Matière</th>
            <th style="text-align:left;width:18%;">Formateur(s)</th>
            <th style="text-align:left;width:14%;">Groupe</th>
            <th style="text-align:left;width:22%;">Options (effectifs)</th>
            <th style="text-align:center;width:10%;">Effectif</th>
            <th style="text-align:center;width:10%;">Sujets</th>
          </tr></thead>
          <tbody>`;

        details.forEach((d, idx) => {
          const optionsStr = d.options.map(o => `${o.option} (${o.effectif})`).join(', ');
          const formateursStr = d.formateurs && d.formateurs.length > 0
            ? d.formateurs.map(f => f.nom).join(', ')
            : '<em style="color:#999;">Non assigné</em>';
          contentHtml += `<tr>
            <td style="text-align:left;">${idx + 1}</td>
            <td style="text-align:left;font-weight:600;">${d.matiere}</td>
            <td style="text-align:left;font-size:${s(7.5)};">${formateursStr}</td>
            <td style="text-align:left;">${d.groupe}</td>
            <td style="text-align:left;font-size:${s(7.5)};">${optionsStr}</td>
            <td style="text-align:center;">${d.effectif}</td>
            <td style="text-align:center;font-weight:700;color:${NAVY};">${d.copies_avec_marge}</td>
          </tr>`;
        });

        contentHtml += `<tr style="font-weight:700;border-top:2px solid #999;background:#F9FAFB;">
            <td colspan="5" style="text-align:right;padding-right:10px;">Sous-total ${niv}</td>
            <td style="text-align:center;">${nivEff}</td>
            <td style="text-align:center;color:${NAVY};">${nivCop}</td>
          </tr>`;

        contentHtml += `</tbody></table>`;
      }

      contentHtml += `</div>`;
    }

    // Grand total
    contentHtml += `<div style="text-align:right;font-size:${s(12)};font-weight:700;color:${NAVY};margin-top:12px;padding:10px;border:2px solid ${GOLD};border-radius:6px;background:#FFFDE7;">
      TOTAL GÉNÉRAL: ${data.total_effectif} étudiants — <span style="color:#B45309;">${data.total_copies_avec_marge} sujets à imprimer</span>
      <span style="font-size:${s(9)};font-weight:400;color:#666;display:block;margin-top:4px;">Marge de sécurité: +${data.marge_pourcentage}% · Formation: ${data.type_formation}</span>
    </div>`;

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Estimation Sujets à Imprimer</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  body { font-family: 'Calibri', 'Segoe UI', sans-serif; font-size: ${s(9)}; color: #1a1a1a; background: #fff; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
  th { background: ${NAVY}; color: ${GOLD}; padding: 5px 8px; border: 1px solid ${NAVY}; font-size: ${s(8)}; text-transform: uppercase; font-weight: 700; }
  td { border: 1px solid #ccc; padding: 4px 8px; font-size: ${s(8)}; }
  tbody tr:nth-child(even) td { background: #F5F7FA; }
</style></head><body>
  <div style="text-align:center;margin-bottom:10px;">
    <div style="font-size:${s(14)};font-weight:700;color:${NAVY};text-transform:uppercase;letter-spacing:1px;">Estimation des sujets à imprimer</div>
    <div style="font-size:${s(9)};color:#666;margin-top:4px;">${data.session} — ${data.annee_academique} — Semestre ${data.semestre}</div>
  </div>
  ${contentHtml}
  <div style="margin-top:16px;text-align:center;font-size:${s(7.5)};color:#999;border-top:1px solid #ddd;padding-top:6px;">
    Document généré le ${today} — Système de Gestion des Notes
  </div>
</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 py-16 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <AlertCircle size={24} />
        <span>Aucun emploi du temps généré pour cette session.</span>
        <span className="text-sm">Générez d'abord l'emploi du temps depuis l'onglet Configuration.</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Contrôles */}
      <div className={`flex flex-wrap gap-4 items-center p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <BookOpen size={20} className="text-indigo-500" />
          <div>
            <div className="font-semibold text-sm">{data.nombre_creneaux} matières</div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{data.total_effectif} étudiants</div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <FileText size={20} className="text-orange-500" />
          <div>
            <div className="font-semibold text-sm">{data.total_copies_avec_marge} sujets</div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>avec marge +{data.marge_pourcentage}%</div>
          </div>
        </div>

        {/* Marge */}
        <div className="flex items-center gap-2">
          <Settings2 size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          <label className="text-xs font-medium">Marge:</label>
          <select
            value={marge}
            onChange={(e) => setMarge(Number(e.target.value))}
            className={`px-2 py-1 rounded border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            {[5, 10, 15, 20, 25, 30].map(v => (
              <option key={v} value={v}>+{v}%</option>
            ))}
          </select>
        </div>

        {/* Type formation */}
        <div className="flex items-center gap-2">
          <Filter size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          <label className="text-xs font-medium">Formation:</label>
          <select
            value={typeFormation}
            onChange={(e) => setTypeFormation(e.target.value)}
            className={`px-2 py-1 rounded border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            <option value="">Tous</option>
            <option value="FI">Formation Initiale (FI)</option>
            <option value="CS">Cours du Soir (CS)</option>
          </select>
        </div>

        {/* Taille impression */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium">Taille ({fontScale}%)</label>
          <input
            type="range" min={70} max={150} step={5}
            value={fontScale}
            onChange={(e) => setFontScale(Number(e.target.value))}
            className="w-20"
          />
        </div>

        <div className="flex-1" />

        <button
          onClick={expandAll}
          className={`text-xs px-3 py-1.5 rounded border ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}
        >
          Tout déplier
        </button>
        <button
          onClick={collapseAll}
          className={`text-xs px-3 py-1.5 rounded border ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}
        >
          Tout replier
        </button>

        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Printer size={16} />
          Imprimer
        </button>
      </div>

      {/* Résumé par niveau */}
      {data.resume_par_niveau.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.resume_par_niveau.map(niv => (
            <div key={niv.niveau} className={`rounded-lg border p-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${NIVEAU_COLORS[niv.niveau] || 'bg-gray-100 text-gray-700'}`}>{niv.niveau}</span>
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{niv.nb_creneaux} matières</span>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Étudiants</div>
                  <div className="text-lg font-bold">{niv.effectif}</div>
                </div>
                <div className="text-right">
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sujets</div>
                  <div className="text-lg font-bold text-orange-600">{niv.copies_avec_marge}</div>
                </div>
              </div>
            </div>
          ))}
          {/* Total */}
          <div className={`rounded-lg border-2 p-3 ${isDark ? 'bg-indigo-900/20 border-indigo-600' : 'bg-indigo-50 border-indigo-300'}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp size={14} className="text-indigo-500" />
              <span className="text-xs font-bold text-indigo-600">TOTAL</span>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Étudiants</div>
                <div className="text-lg font-bold">{data.total_effectif}</div>
              </div>
              <div className="text-right">
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sujets</div>
                <div className="text-lg font-bold text-orange-600">{data.total_copies_avec_marge}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Détail groupé par pôle → niveau */}
      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([pole, niveaux]) => {
        const ps = poleStats[pole];
        const pColors = isDark
          ? (POLE_COLORS_DARK[pole] || { bg: 'bg-gray-800', text: 'text-gray-300', border: 'border-gray-600' })
          : (POLE_COLORS[pole] || { bg: 'bg-gray-50', text: 'text-gray-800', border: 'border-gray-200' });

        return (
          <div key={pole} className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700' : pColors.border}`}>
            {/* Header pôle */}
            <div className={`px-4 py-3 ${isDark ? 'bg-gray-750' : pColors.bg}`}>
              <div className="flex items-center justify-between">
                <span className={`font-bold text-base ${isDark ? pColors.text : pColors.text}`}>{pole}</span>
                <div className={`flex items-center gap-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <span>{ps.matieres} matières</span>
                  <span className="flex items-center gap-1"><Users size={14} /> {ps.effectif} étudiants</span>
                  <span className="font-bold text-orange-600">{ps.copies} sujets</span>
                </div>
              </div>
            </div>

            {/* Niveaux dans ce pôle */}
            {['L1', 'L2', 'L3'].filter(n => niveaux[n]).map(niv => {
              const details = niveaux[niv];
              const sectionKey = `${pole}__${niv}`;
              const isExpanded = expandedSections.has(sectionKey);
              const nivEff = details.reduce((s, d) => s + d.effectif, 0);
              const nivCop = details.reduce((s, d) => s + d.copies_avec_marge, 0);

              return (
                <div key={niv}>
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left border-t ${
                      isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`px-2.5 py-0.5 rounded text-xs font-bold ${NIVEAU_COLORS[niv] || 'bg-gray-100 text-gray-700'}`}>{niv}</span>
                      <span className="text-sm font-medium">{details.length} matières</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm"><span className="font-medium">{nivEff}</span> étudiants</span>
                      <span className="text-sm font-bold text-orange-600">{nivCop} sujets</span>
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-3">
                      <table className={`w-full border-collapse text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        <thead>
                          <tr className={isDark ? 'bg-gray-700' : 'bg-gray-100'}>
                            <th className="text-left px-3 py-2 font-medium w-6">N°</th>
                            <th className="text-left px-3 py-2 font-medium">Matière</th>
                            <th className="text-left px-3 py-2 font-medium">Formateur(s)</th>
                            <th className="text-left px-3 py-2 font-medium">Groupe</th>
                            <th className="text-left px-3 py-2 font-medium">Options (effectifs)</th>
                            <th className="text-center px-3 py-2 font-medium">Effectif</th>
                            <th className="text-center px-3 py-2 font-medium">Sujets</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.map((d, idx) => (
                            <tr key={d.creneau_id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                              <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                              <td className="px-3 py-2 font-medium">{d.matiere}</td>
                              <td className="px-3 py-2">
                                {d.formateurs && d.formateurs.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {d.formateurs.map(f => (
                                      <span key={f.enseignant_id} className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                                        {f.nom}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Non assigné</span>
                                )}
                              </td>
                              <td className="px-3 py-2">{d.groupe}</td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {d.options.map(o => (
                                    <span key={o.option} className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                      {o.option} <span className="font-bold">({o.effectif})</span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center font-medium">{d.effectif}</td>
                              <td className="px-3 py-2 text-center font-bold text-orange-600">{d.copies_avec_marge}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className={`border-t-2 font-medium ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                            <td className="px-3 py-2" colSpan={5} style={{ textAlign: 'right' }}>Sous-total {niv}</td>
                            <td className="px-3 py-2 text-center font-bold">{nivEff}</td>
                            <td className="px-3 py-2 text-center font-bold text-orange-600">{nivCop}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Alerte si 0 matières */}
      {data.nombre_creneaux === 0 && (
        <div className={`text-center py-12 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <AlertCircle size={40} className="mx-auto text-gray-400 mb-3" />
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Aucun créneau actif trouvé pour cette session.</p>
        </div>
      )}
    </div>
  );
};

export default EstimationSujets;
